import PropTypes from 'prop-types';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
    ReferenceLine
} from 'recharts';
import {
    buildQuarterChartData,
    chartSeriesCaption,
    formatChartValue,
    formatChartAxisDate
} from '../../utils/chartDataBuilder.js';

const STATUS_STROKE = {
    green: '#16a34a',
    yellow: '#f59e0b',
    red: '#dc2626',
    gray: '#94a3b8'
};

function dayStartMs(date) {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    ).getTime();
}

/* eslint-disable react/prop-types -- Recharts tooltip injects active/payload */
function CompactTooltip({ active, payload, item }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
        <div className="kpi-trend-tooltip">
            <div className="kpi-trend-tooltip__date">{row.tooltipDate}</div>
            {row.actual != null ? (
                <div>
                    {row.isReading ? 'Logged' : 'Actual'}:{' '}
                    <strong>{formatChartValue(item, row.actual)}</strong>
                </div>
            ) : null}
            <div>
                Pace to goal:{' '}
                <strong>{formatChartValue(item, row.pace)}</strong>
            </div>
        </div>
    );
}

function FullTooltip({ active, payload, item }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
        <div className="kpi-trend-tooltip kpi-trend-tooltip--full">
            <div className="kpi-trend-tooltip__date">{row.tooltipDate}</div>
            {row.actual != null ? (
                <div>
                    {row.isReading ? 'Logged' : 'Actual'}:{' '}
                    <strong>{formatChartValue(item, row.actual)}</strong>
                </div>
            ) : null}
            <div>
                Pace to goal:{' '}
                <strong>{formatChartValue(item, row.pace)}</strong>
            </div>
        </div>
    );
}
/* eslint-enable react/prop-types */

export default function KpiTrendChart({
    item,
    reportingPeriod,
    status,
    variant
}) {
    const model = buildQuarterChartData(item, reportingPeriod);
    const { data, periodStart, periodEnd, todayMs, hasActual } = model;
    const stroke = STATUS_STROKE[status] || STATUS_STROKE.gray;
    const isCompact = variant === 'compact';
    const height = isCompact ? 52 : 220;

    if (!periodStart || !periodEnd || !data.length) {
        return (
            <div
                className={`kpi-trend-chart kpi-trend-chart--${variant} kpi-trend-chart--empty`}
                aria-hidden={isCompact}>
                <p className="kpi-trend-chart__empty">No year timeline</p>
            </div>
        );
    }

    const domainStart = dayStartMs(periodStart);
    const domainEnd = dayStartMs(periodEnd);

    const chart = (
        <LineChart
            data={data}
            margin={
                isCompact
                    ? { top: 4, right: 4, left: 0, bottom: 0 }
                    : { top: 8, right: 24, left: 8, bottom: 8 }
            }>
            {!isCompact ? (
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
            ) : null}
            <XAxis
                type="number"
                dataKey="t"
                domain={[domainStart, domainEnd]}
                tick={isCompact ? false : { fontSize: 11, fill: '#64748b' }}
                axisLine={!isCompact}
                tickLine={!isCompact}
                tickFormatter={(ts) => formatChartAxisDate(new Date(ts))}
                interval="preserveStartEnd"
            />
            {!isCompact ? (
                <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    width={48}
                    tickFormatter={(v) => formatChartValue(item, v)}
                />
            ) : null}
            <Tooltip
                content={
                    isCompact ? (
                        <CompactTooltip item={item} />
                    ) : (
                        <FullTooltip item={item} />
                    )
                }
            />
            {!isCompact ? <Legend /> : null}
            {todayMs != null ? (
                <ReferenceLine
                    x={todayMs}
                    stroke="#94a3b8"
                    strokeDasharray="3 3"
                    label={
                        isCompact
                            ? undefined
                            : {
                                  value: 'Today',
                                  position: 'insideTopRight',
                                  fill: '#64748b',
                                  fontSize: 11
                              }
                    }
                />
            ) : null}
            <Line
                type="monotone"
                dataKey="pace"
                stroke="#94a3b8"
                strokeWidth={isCompact ? 1.5 : 2}
                strokeDasharray="5 4"
                dot={false}
                name="Pace to goal"
                connectNulls
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="actual"
                stroke={stroke}
                strokeWidth={isCompact ? 2 : 2.5}
                dot={isCompact ? false : { r: 3, fill: stroke }}
                activeDot={isCompact ? false : { r: 5 }}
                name="Actual"
                connectNulls
                isAnimationActive={false}
            />
        </LineChart>
    );

    if (isCompact) {
        return (
            <div
                className="kpi-trend-chart kpi-trend-chart--compact"
                aria-hidden>
                <ResponsiveContainer width="100%" height={height} minWidth={0}>
                    {chart}
                </ResponsiveContainer>
                <div className="kpi-trend-chart__axis-labels">
                    <span>{formatChartAxisDate(periodStart)}</span>
                    <span>{formatChartAxisDate(periodEnd)}</span>
                </div>
                <p className="kpi-trend-chart__legend-mini">
                    <span
                        className="kpi-trend-chart__swatch kpi-trend-chart__swatch--actual"
                        style={{ background: stroke }}
                    />
                    Actual
                    <span className="kpi-trend-chart__swatch kpi-trend-chart__swatch--pace" />
                    Pace to goal
                </p>
            </div>
        );
    }

    return (
        <div className="card chart kpi-trend-chart kpi-trend-chart--full">
            <h4>Year progress</h4>
            <p className="chart-caption">
                {chartSeriesCaption(item, reportingPeriod)}
            </p>
            <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height={height} minWidth={0}>
                    {chart}
                </ResponsiveContainer>
            </div>
            {!hasActual ? (
                <p className="chart-empty kpi-trend-chart__hint">
                    Log updates or wait for integrations to build a fuller
                    actual line.
                </p>
            ) : null}
        </div>
    );
}

KpiTrendChart.propTypes = {
    item: PropTypes.object.isRequired,
    reportingPeriod: PropTypes.object,
    status: PropTypes.string,
    variant: PropTypes.oneOf(['compact', 'full'])
};

KpiTrendChart.defaultProps = {
    reportingPeriod: undefined,
    status: 'gray',
    variant: 'compact'
};
